// src/Login.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import PageHeader from './PageHeader'; // NEW: Replaces old Navbar

const API_BASE_URL = 'https://kigaawater.inkenya.co.ke/api';

// Status message renderer
const renderStatus = (status, type) => {
  const baseStyle = {
    padding: '12px',
    borderRadius: '8px',
    fontWeight: '600',
    marginTop: '15px',
    display: 'flex',
    alignItems: 'center',
    minHeight: '40px',
  };

  let style = {};
  let prefix = '';

  switch (type) {
    case 'success':
      style = { ...baseStyle, backgroundColor: '#d4edda', color: '#155724' };
      prefix = 'Success';
      break;
    case 'error':
      style = { ...baseStyle, backgroundColor: '#f8d7da', color: '#721c24' };
      prefix = 'Error';
      break;
    case 'loading':
      style = { ...baseStyle, backgroundColor: '#cce5ff', color: '#004085' };
      prefix = 'Loading';
      break;
    default:
      style = { ...baseStyle, backgroundColor: '#e2e3e5', color: '#383d41' };
      prefix = 'Info';
      break;
  }

  return (
    <div style={style}>
      <span style={{ marginRight: '10px' }}>{prefix}</span>
      {status}
    </div>
  );
};

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [mode, setMode] = useState('loading');
  const [status, setStatus] = useState(null);

  const [setupData, setSetupData] = useState({
    company_name: '',
    financial_year_start_month: 1,
    address: '',
    phone_number: '',
    first_name: '',
    last_name: '',
    admin_phone_number: '',
    password: '',
    confirm_password: '',
  });

  const [loginData, setLoginData] = useState({
    phone_number: '',
    password: '',
  });

  // Check if setup is needed
  useEffect(() => {
    const checkSetup = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/check-setup`);
        setMode(res.data.isSetup ? 'login' : 'setup');
      } catch (err) {
        setStatus(renderStatus('Connection failed. Trying login...', 'error'));
        setMode('login');
      }
    };
    checkSetup();
  }, []);

  const handleSetupChange = (e) => {
    const { name, value } = e.target;
    setSetupData(prev => ({ ...prev, [name]: value }));
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
  };

  // === SETUP SUBMIT ===
  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    // Password match
    if (setupData.password !== setupData.confirm_password) {
      setStatus(renderStatus('Passwords do not match.', 'error'));
      return;
    }

    // Required fields
    const required = {
      company_name: 'string',
      financial_year_start_month: 'number',
      first_name: 'string',
      last_name: 'string',
      admin_phone_number: 'string',
      password: 'string',
    };

    const missing = [];
    for (const [field, type] of Object.entries(required)) {
      const value = setupData[field];
      if (type === 'string' && (!value || value.trim() === '')) {
        missing.push(field);
      } else if (type === 'number' && (value === '' || isNaN(value))) {
        missing.push(field);
      }
    }

    if (missing.length > 0) {
      setStatus(renderStatus(`Required: ${missing.join(', ')}`, 'error'));
      return;
    }

    setStatus(renderStatus('Setting up system...', 'loading'));

    const payload = {
      company_name: setupData.company_name.trim(),
      financial_year_start_month: parseInt(setupData.financial_year_start_month, 10),
      address: setupData.address.trim(),
      phone_number: setupData.phone_number.trim(),
      first_name: setupData.first_name.trim(),
      last_name: setupData.last_name.trim(),
      admin_phone_number: setupData.admin_phone_number.trim(),
      password: setupData.password,
    };

    try {
      await axios.post(`${API_BASE_URL}/initial-setup`, payload);
      setStatus(renderStatus('Setup complete! Redirecting to login...', 'success'));
      setTimeout(() => setMode('login'), 1500);
    } catch (err) {
      const msg = err.response?.data?.error || 'Setup failed.';
      setStatus(renderStatus(msg, 'error'));
    }
  };

  // === LOGIN SUBMIT ===
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setStatus(renderStatus('Logging in...', 'loading'));

    try {
      const res = await axios.post(`${API_BASE_URL}/login`,
  loginData,
  { withCredentials: true });
      
      // 🛑 FIX 1: Destructure using 'firstName' and 'lastName' (camelCase) 
      // to match your backend response.
      const { token, role_id, employee_id, firstName, lastName } = res.data; 
      
      // Construct the full name. It will now use the correct values.
      const fullName = `${firstName} ${lastName}`;
      
      // 🛑 FIX 2: Ensure the key used here is 'employeeFullName'
      localStorage.setItem('employeeFullName', fullName); 
      
      login(token, role_id, employee_id); // Pass existing credentials to the auth context
      
      setStatus(renderStatus('Login successful!', 'success'));
      setTimeout(() => {
        navigate('/dashboard');
      }, 800);
    } catch (err) {
      setStatus(renderStatus(err.response?.data?.error || 'Invalid credentials.', 'error'));
    }
  };

  // Loading state
  if (mode === 'loading') {
    return (
      <>
        <PageHeader title="Water Society Admin Login" />
        <div style={{ padding: '50px', textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
          Loading...
        </div>
      </>
    );
  }

  return (
    <>
      {/* NEW: Blue Header (Same as Board Page) */}
      <PageHeader title="Water Society Admin Login" />

      {/* Form Container */}
      <div style={styles.container}>
        <h2 style={styles.header}>
          {mode === 'setup' ? 'Initial System Setup' : 'Water Society Admin Login'}
        </h2>

        {status}

        {mode === 'setup' ? (
          <form onSubmit={handleSetupSubmit} style={styles.form}>
            <h3>Company Details</h3>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Company Name *</label>
              <input style={styles.input} type="text" name="company_name" value={setupData.company_name} onChange={handleSetupChange} required />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Financial Year Start Month *</label>
              <select style={styles.input} name="financial_year_start_month" value={setupData.financial_year_start_month} onChange={handleSetupChange} required>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('en', { month: 'long' })}</option>
                ))}
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Address</label>
              <input style={styles.input} type="text" name="address" value={setupData.address} onChange={handleSetupChange} />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Company Phone</label>
              <input style={styles.input} type="text" name="phone_number" value={setupData.phone_number} onChange={handleSetupChange} />
            </div>

            <h3>Super Admin</h3>
            <div style={styles.inputGroup}>
              <label style={styles.label}>First Name *</label>
              <input style={styles.input} type="text" name="first_name" value={setupData.first_name} onChange={handleSetupChange} required />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Last Name *</label>
              <input style={styles.input} type="text" name="last_name" value={setupData.last_name} onChange={handleSetupChange} required />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Phone Number *</label>
              <input style={styles.input} type="text" name="admin_phone_number" value={setupData.admin_phone_number} onChange={handleSetupChange} required />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Password *</label>
              <input style={styles.input} type="password" name="password" value={setupData.password} onChange={handleSetupChange} required />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Confirm Password *</label>
              <input style={styles.input} type="password" name="confirm_password" value={setupData.confirm_password} onChange={handleSetupChange} required />
            </div>
            <button type="submit" style={styles.button}>Complete Setup</button>
          </form>
        ) : (
          <form onSubmit={handleLoginSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Phone Number *</label>
              <input style={styles.input} type="text" name="phone_number" value={loginData.phone_number} onChange={handleLoginChange} required />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Password *</label>
              <input style={styles.input} type="password" name="password" value={loginData.password} onChange={handleLoginChange} required />
            </div>
            <button type="submit" style={styles.button}>Login</button>
          </form>
        )}
      </div>
    </>
  );
}

const styles = {
  container: {
    maxWidth: '600px',
    margin: '40px auto',
    padding: '30px',
    backgroundColor: '#f9f9f9',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    fontFamily: 'Inter, sans-serif',
  },
  header: {
    textAlign: 'center',
    color: '#007bff',
    marginBottom: '25px',
    fontSize: '1.8em',
    borderBottom: '2px solid #eef2f5',
    paddingBottom: '10px',
  },
  form: { display: 'flex', flexDirection: 'column' },
  inputGroup: { marginBottom: '15px' },
  label: { display: 'block', marginBottom: '5px', fontWeight: '600', color: '#333' },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    boxSizing: 'border-box',
    fontSize: '16px',
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '18px',
    cursor: 'pointer',
    fontWeight: 'bold',
    marginTop: '10px',
  },
};

export default Login;